<template>
  <v-dialog v-model="open" persistent max-width="980px" scrollable>
    <v-card class="profile-dialog-card entity-form-dialog">
      <EntityDialogHeader eyebrow="Billing dry run" title="Upcoming tuition invoices" subtitle="A read-only preview. No invoices or charges will be created." icon="mdi-receipt-text-clock-outline" @close="close" />
      <v-divider />
      <v-card-text class="profile-dialog-body entity-form-dialog__body preview-body">
        <v-skeleton-loader v-if="loading" type="article, table" />
        <template v-else-if="preview">
          <section class="entity-form-dialog__section preview-summary">
            <div><span>Billing day</span><strong>{{ ordinal(preview.billingDay) }} of each month</strong></div>
            <div><span>Payment category</span><strong>{{ preview.paymentCategory ? preview.paymentCategory.name : "Not configured" }}</strong></div>
            <div><span>Merchant</span><strong>{{ merchantLabel }}</strong></div>
          </section>
          <v-expansion-panels accordion flat>
            <v-expansion-panel v-for="invoice in preview.invoices" :key="invoice.month" class="preview-month">
              <v-expansion-panel-header>
                <div class="month-heading"><div><strong>{{ invoice.label }}</strong><span>Invoice and due {{ formatDate(invoice.dueDate) }}</span></div><div class="month-heading__totals"><strong>{{ invoice.total | currency }} invoice</strong><span>{{ invoice.parentTotal | currency }} parent · {{ invoice.charityTotal | currency }} Masjid · {{ invoice.zakatTotal | currency }} Zakat · {{ invoice.payrollTotal | currency }} payroll</span></div></div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-for="line in invoice.lines" :key="`${invoice.month}-${line.classStudentId}`" class="preview-line" :class="{ 'preview-line--skipped': !line.included }">
                  <div><strong>{{ line.studentName }}</strong><span>{{ line.departmentName }} · {{ line.scheduleSource === 'STUDENT_OVERRIDE' ? 'Student override' : 'Department schedule' }}</span></div>
                  <div class="preview-line__amount"><strong>{{ line.amount + line.charity + line.zakat + line.payrollDeduction | currency }}</strong><span v-if="!line.included">{{ line.reason }}</span><span v-else>{{ line.amount | currency }} parent<span v-if="line.charity"> · {{ line.charity | currency }} Masjid</span><span v-if="line.zakat"> · {{ line.zakat | currency }} Zakat</span><span v-if="line.payrollDeduction"> · {{ line.payrollDeduction | currency }} payroll<span v-if="line.payrollTeacherName"> ({{ line.payrollTeacherName }})</span></span></span></div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </template>
      </v-card-text>
      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <v-btn v-if="preview" outlined color="primary" @click="emailOpen = true"><v-icon left>mdi-email-outline</v-icon>Preview parent email</v-btn>
        <v-spacer />
        <v-btn text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="emailOpen" max-width="720px" scrollable>
      <v-card class="email-preview-dialog">
        <EntityDialogHeader eyebrow="Parent communication" title="Billing notice email" subtitle="This is the email parents receive two days before collection." icon="mdi-email-check-outline" @close="emailOpen = false" />
        <v-divider />
        <v-card-text class="email-preview-body">
          <iframe title="Parent billing email preview" :srcdoc="preview ? preview.emailPreview : ''" sandbox />
        </v-card-text>
        <v-card-actions class="profile-dialog-actions entity-form-dialog__actions"><v-spacer /><v-btn text @click="emailOpen = false">Close preview</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import customerService from "@/modules/Customer/service";
export default {
  name: "TuitionBillingPreview",
  props: { value: Boolean, customerId: { required: true } },
  data: () => ({ loading: false, preview: null, emailOpen: false }),
  computed: { open: { get() { return this.value; }, set(value) { if (!value) this.close(); } }, merchantLabel() { const merchant = this.preview && this.preview.paymentCategory && this.preview.paymentCategory.merchant; return merchant ? `${merchant.name} (${this.providerName(merchant.provider)})` : "Not configured"; } },
  watch: { value(value) { if (value) this.load(); } },
  methods: {
    async load() { this.loading = true; try { const response = await customerService.getTuitionBillingPreview(this.customerId); this.preview = response.data; } catch (error) { this.$swal.fire({ icon: "error", title: "Unable to build billing preview", text: (error.data && error.data.message) || "Please try again." }); this.close(); } finally { this.loading = false; } },
    close() { this.$emit("input", false); },
    formatDate(value) { return this.$moment(`${value}T00:00:00`).format("MMM D, YYYY"); },
    providerName(value) { return value === "PAYCOMPASS" ? "PayCompass" : value === "STRIPE" ? "Stripe" : value || "Unknown"; },
    ordinal(value) { const number = Number(value); const suffix = number % 10 === 1 && number % 100 !== 11 ? "st" : number % 10 === 2 && number % 100 !== 12 ? "nd" : number % 10 === 3 && number % 100 !== 13 ? "rd" : "th"; return `${number}${suffix}`; },
  },
};
</script>

<style scoped>
.preview-body { min-height: 420px; }
.preview-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.preview-summary div, .month-heading div, .preview-line div { display: flex; flex-direction: column; }
.preview-summary span, .month-heading span, .preview-line span { color: #7f8c82; font-size: 12px; }
.preview-month { border: 1px solid #e2e9e3; margin-bottom: 8px; border-radius: 10px; }
.month-heading, .preview-line { display: flex; width: 100%; justify-content: space-between; align-items: center; gap: 16px; }
.preview-line { padding: 11px 0; border-top: 1px solid #edf1ed; }
.preview-line--skipped { opacity: .55; }
.preview-line__amount { text-align: right; }
.month-heading__totals { align-items: flex-end; text-align: right; }
.email-preview-dialog { overflow: hidden; border-radius: 18px !important; }
.email-preview-body { padding: 0 !important; background: #f3f7f3; }
.email-preview-body iframe { display: block; width: 100%; height: min(720px, 72vh); border: 0; background: #f3f7f3; }
@media (max-width: 600px) { .preview-summary { grid-template-columns: 1fr; } }
</style>
